import { Button, FormControl, FormSelect, Spinner } from 'react-bootstrap';
import { getArticleInfo } from '../../services/CartService';
import { StatusIcon } from './StatusIcon';
import { Table } from './Table';
import { t } from '../../services/translationService';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import axios from 'axios';


export interface OrderFormDistributor {
  id?: number;
  name: string;
  shortName: string;
  stockStatus: string;
}

export interface OrderFormRow {
  catalogEntryCode: string;
  distributors?: OrderFormDistributor[];
  isInvalid?: boolean;
  isLoading?: boolean,
  isFetched?: boolean;
  isSellable?: boolean;
  populated?: boolean;
  quantity?: number;
  reference?: string;
  selectedDistributorShortName?: string;
  title?: string;
}

interface Props {
  rows: OrderFormRow[],
  setRows: Dispatch<SetStateAction<OrderFormRow[]>>,
  isValid: Dispatch<SetStateAction<boolean>>,
  populated?: boolean,
  autoPopulate?: boolean
}

export const OrderForm: React.FC<Props> = ({
  rows,
  setRows,
  populated,
  isValid,
  autoPopulate
}: Props) => {

  useEffect(() => {
    isValid(rows.every((row: OrderFormRow) => !row.isInvalid)); // Simplified validation check
  }, [rows, isValid]); // Avoid unnecessary re-renders

  const rowFetchStatus = useRef(new Set());

  const unsellableStatuses = new Set(["blocked", "willnotbepublished", "definitelyoutofstock"]);

  useEffect(() => {
    if (autoPopulate && rows.length) {
      rows.forEach((row: OrderFormRow, index: number) => {
        if (!row.isFetched && !rowFetchStatus.current.has(index)) {
          rowFetchStatus.current.add(index);
          populateRow(index);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, autoPopulate]);

  const populateRow = (rowIndex: number) => {
    const currentRow = rows[rowIndex];

    if (!currentRow?.catalogEntryCode?.length) {
      return;
    }

    setRows((prevRows: OrderFormRow[]) => {
      const updatedRows = [...prevRows];

      updatedRows[rowIndex] = {
        ...prevRows[rowIndex],
        isLoading: true,
      };

      return updatedRows;
    });

    // If it's the last row and not populated, add a new row
    if (!populated && rowIndex === rows.length - 1) {
      setRows((prevRows: OrderFormRow[]) => [
        ...prevRows,
        {
          catalogEntryCode: '',
          quantity: undefined,
          distributors: [],
          selectedDistributorShortName: '',
          title: '',
          reference: '',
        },
      ]);
    }

    // Fetch article info and update the row
    getArticleInfo(currentRow.catalogEntryCode)
      .then((data: OrderFormRow) => {
        setRows((prevRows) =>
          prevRows.map((row, index) => {
            if (index !== rowIndex) return row; // Keep unchanged rows

            // Find a matching distributor by shortName
            const matchedDistributor = data?.distributors?.find(
              distributor => distributor.shortName === row.selectedDistributorShortName
            );

            return {
              ...row, 
              title: data?.title, 
              distributors: data?.distributors, 
              selectedDistributorShortName: matchedDistributor
                ? row.selectedDistributorShortName
                : data?.selectedDistributorShortName, 
              isInvalid: !data?.title || !data?.distributors || !data.isSellable, // Mark as invalid if required fields are missing
              isSellable: data?.isSellable, 
              isLoading: false, 
            };
          })
        );
      })
      .catch((error: unknown) => {
        let errorMessage = "Ett okänt fel uppstod.";

        if (axios.isAxiosError(error)) {
          // If the error is an AxiosError, extract meaningful information
          errorMessage = error.response?.data?.title || error.message;
        } else if (error instanceof Error) {
          // If it's a generic JS error
          errorMessage = error.message;
        }

        console.error("Error fetching article info:", error);

        setRows((prevRows: OrderFormRow[]) => {
          const updatedRows = [...prevRows];
          updatedRows[rowIndex] = {
            ...prevRows[rowIndex],
            title: errorMessage,
            isInvalid: true,
            isLoading: false, // Reset loading state on error
          };
          return updatedRows;
        });
      });
  };

  const updateField = (rowIndex: number, field: string, value: any) => {
    setRows((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows[rowIndex] = { ...prevRows[rowIndex], [field]: value };

      return updatedRows;
    });
  };

  const getErrorMessage = (row: OrderFormRow): string | null => {
    const selectedDistributor = row?.distributors?.find(distributor => distributor.shortName === row.selectedDistributorShortName);

    return selectedDistributor && unsellableStatuses.has(selectedDistributor.stockStatus?.toLocaleLowerCase() ?? "")
      ? t('articleHasBadStatus')
      : t('articleHasWrongType');
  }

  const distributorOnChange = (event: React.ChangeEvent<HTMLSelectElement>, rowIndex: number) => {
    const distributorShortName: string = event.target?.value ?? "";
    const currentRow: OrderFormRow | undefined = rows[rowIndex]
    const selectedDistributor: OrderFormDistributor | undefined = rows[rowIndex]?.distributors?.find(distributor => distributor.shortName && distributor.shortName === distributorShortName)
    const isSellable: boolean = !unsellableStatuses.has(selectedDistributor?.stockStatus?.toLocaleLowerCase() ?? "");

    setRows((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows[rowIndex] = {
        ...prevRows[rowIndex],
        "selectedDistributorShortName": distributorShortName,
        "isSellable": isSellable,
        "isInvalid": !currentRow?.title || !currentRow?.distributors || !isSellable,
      };

      return updatedRows;
    });
  };

  return (
    <Table
      width={991}
      columnGap={2}
      smallTableHeadFont
      firstColumnBold={false}
      striped
      columns={[
        { title: t('articleNumber'), width: '12rem' },
        { title: t('amount'), width: '4rem' },
        { title: t('distributor'), width: '8rem' },
        {
          title: t('title'),
          width: 'calc(100% - 42rem)',
          truncated: true,
        },
        { title: t('status'), width: '6rem' },
        { title: t('reference'), width: '8rem' },
        { title: '', width: '4rem' },
      ]}
      items={rows.map((currentRow: OrderFormRow, rowIndex: number) => ({
        columns: [
          <FormControl
            onInput={(e) =>
              updateField(
                rowIndex,
                'catalogEntryCode',
                (e.target as HTMLTextAreaElement)?.value ?? ""
              )
            }
            onBlur={() => populateRow(rowIndex)}
            value={currentRow.catalogEntryCode || ""}
          />,
          <FormControl
            onInput={(e) => {
              const value: string = (e.target as HTMLTextAreaElement).value;
              return updateField(rowIndex, 'quantity', isNaN(+value) ? null : +value);
            }}
            value={currentRow.quantity ?? ""}
          />,
          <FormSelect
            value={currentRow.selectedDistributorShortName}
            disabled={!currentRow.distributors?.length}
            onChange={(event) => distributorOnChange(event, rowIndex)}
          >
            {currentRow.distributors &&
              currentRow.distributors.map((distributor: OrderFormDistributor, index) => (
                <option key={`${distributor.shortName}-${index}`} value={distributor.shortName ?? ""}>{distributor.name}</option>
              ))}
          </FormSelect>,
          <>
            {currentRow.isLoading ? (
              <span className="text-primary">
                <Spinner animation="border" size="sm" />
              </span>
            ) : (
              <span className={`${currentRow.isInvalid ? 'text-danger' : ''}`}>
                {currentRow.title || (currentRow.isInvalid && t('articleMissing'))}
                {currentRow.title && (currentRow.isInvalid && !currentRow.isSellable && <p className="small">{getErrorMessage(currentRow)}</p>)}
              </span>
            )}
          </>,
          currentRow.distributors &&
          currentRow.distributors.find(
            (distributor: OrderFormDistributor) => distributor.shortName === currentRow.selectedDistributorShortName
          ) && (
            <div className="d-flex justify-content-center">
              <StatusIcon
                status={
                  currentRow.selectedDistributorShortName &&
                  currentRow.distributors.find(
                    (distributor: OrderFormDistributor) => distributor.shortName === currentRow.selectedDistributorShortName
                  )?.stockStatus
                }
              />
            </div>
          ),
          <FormControl
            onInput={(e) =>
              updateField(
                rowIndex,
                'reference',
                (e.target as HTMLTextAreaElement)?.value ?? ""
              )
            }
            value={currentRow.reference}
          />,
          rows.length > rowIndex + (populated ? 0 : 1) && (
            <Button
              variant="outline-danger"
              className="w-100 border-0"
              onClick={() => {
                setRows((prev) =>
                  prev.filter((_, index: number) => index !== rowIndex)
                );
              }}
            >
              <i className="bi bi-trash3" />
            </Button>
          ),
        ],
      }))}
    />
  );
};
